import { useEffect, useMemo, useState } from 'react';

import { isExperimentEnabled, expValEquals } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

export enum BriefingNudgeExperimentState {
	CONTROL = 'control',
	NUDGE = 'nudge',
	NUDGE_AUTOPLAY = 'nudge_autoplay',
}

type AudioFeatureFlags = {
	isAIPolicyAndBrowserSupportEnabled: boolean;
	isAudioV3Enabled: boolean;
	isAudioBylineEntrypointEnabled: boolean;
	audioBriefingNudgeExperimentState: BriefingNudgeExperimentState;
};

export const useAudioFeatureFlags = (): AudioFeatureFlags => {
	const { isAdminHubAIEnabled, isAIEarlyAccessEnabled, isAtlassianHostedLLMOnlyEnabled } =
		useSessionData();

	// Deferred: MediaSource is unavailable during SSR
	const [audioSupportedByBrowser, setAudioSupportedByBrowser] = useState(false);
	useEffect(() => {
		setAudioSupportedByBrowser(
			typeof MediaSource !== 'undefined' && MediaSource.isTypeSupported('audio/mpeg'),
		);
	}, []);

	const { isAIPolicyEnabled, isAudioV3EnabledForPolicy, isAudioBylineEntrypointEnabled } =
		useMemo(() => {
			const aiPolicyEnabled =
				!isAtlassianHostedLLMOnlyEnabled && isAdminHubAIEnabled && isAIEarlyAccessEnabled;

			const audioV3EnabledForPolicy = aiPolicyEnabled && isExperimentEnabled('confluence_audio_v3');

			return {
				isAIPolicyEnabled: aiPolicyEnabled,
				isAudioV3EnabledForPolicy: audioV3EnabledForPolicy,
				// Keep byline visibility stable through SSR and hydration. ListenByline checks browser
				// support after hydration and disables its trigger when audio playback is unsupported.
				isAudioBylineEntrypointEnabled: audioV3EnabledForPolicy,
			};
		}, [isAdminHubAIEnabled, isAIEarlyAccessEnabled, isAtlassianHostedLLMOnlyEnabled]);

	const isAudioV3Enabled = isAudioV3EnabledForPolicy && audioSupportedByBrowser;

	const audioBriefingNudgeExperimentState = useMemo(() => {
		if (!isAudioV3Enabled) {
			return BriefingNudgeExperimentState.CONTROL;
		}

		if (expValEquals('confluence_audio_v3_briefing_nudge', 'variant', 'nudge')) {
			return BriefingNudgeExperimentState.NUDGE;
		} else if (expValEquals('confluence_audio_v3_briefing_nudge', 'variant', 'nudge_autoplay')) {
			return BriefingNudgeExperimentState.NUDGE_AUTOPLAY;
		} else {
			return BriefingNudgeExperimentState.CONTROL;
		}
	}, [isAudioV3Enabled]);

	return {
		isAIPolicyAndBrowserSupportEnabled: isAIPolicyEnabled && audioSupportedByBrowser,
		isAudioV3Enabled,
		isAudioBylineEntrypointEnabled,
		audioBriefingNudgeExperimentState,
	};
};
